import {
  taskGroupBySchema,
  taskListFilterSchema,
  taskSortFieldSchema,
  sortDirectionSchema,
  type TaskGroupBy,
  type TaskListFilter,
  type TaskListQuery,
} from '@pm-tool/shared';
import { z } from 'zod';

/**
 * URL state of the task list (TASK-04): filters, sorting, grouping and the open task. Invalid
 * values are dropped one by one, so an old or edited link still opens the list.
 */
export type ListSearch = TaskListFilter & {
  sort?: TaskListQuery['sort'];
  dir?: TaskListQuery['dir'];
  group?: TaskGroupBy;
  /** Key of the task in the side panel. */
  task?: string;
};

const fieldSchemas = {
  ...taskListFilterSchema.shape,
  sort: taskSortFieldSchema,
  dir: sortDirectionSchema,
  group: taskGroupBySchema,
  task: z.string().max(20),
};

export function parseListSearch(raw: Record<string, unknown>): ListSearch {
  const result: Record<string, unknown> = {};
  for (const [key, schema] of Object.entries(fieldSchemas)) {
    const value = raw[key];
    if (value === undefined || value === '') continue;
    const parsed = (schema as z.ZodType).safeParse(value); // each entry is a Zod schema
    if (parsed.success && parsed.data !== undefined) result[key] = parsed.data;
  }
  return result;
}

/** Filters only (no sort, grouping or open task), e.g. to tell whether any filter is active. */
export function filtersOf(search: ListSearch): TaskListFilter {
  const filters = { ...search };
  delete filters.sort;
  delete filters.dir;
  delete filters.group;
  delete filters.task;
  return filters;
}

export function hasFilters(search: ListSearch): boolean {
  return Object.values(filtersOf(search)).some(
    (v) => v !== undefined && v !== false && !(Array.isArray(v) && v.length === 0),
  );
}

/** API query of the list (without grouping and the open task). */
export function toListQuery(search: ListSearch): Partial<TaskListQuery> {
  const query = { ...search };
  delete query.group;
  delete query.task;
  return query;
}

/** The list state with another (or no) open task. */
export function withTask(search: ListSearch, key: string | undefined): ListSearch {
  const next = { ...search };
  if (key === undefined) delete next.task;
  else next.task = key;
  return next;
}
